import { useProducts } from '@/features/quotes/hooks/common/use-products.hook';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { f, formatDateToBrazil, useFilters } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
  Avatar,
  DataTable,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, SquarePen, User } from 'lucide-react';
import { useMemo } from 'react';
import { useProposalAnalyses } from '../hooks';
import {
  type ProposalAnalysisAction,
  createProposalAnalysesColumns,
} from './proposal-analyses-table-columns';
import type { ProposalAnalysisListItem } from '../types';

// A tela é escopada em "Proposta em Análise": o dropdown reflete o status da
// jornada, e "Todos" continua caindo no mesmo status.
const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Em Análise', value: String(PortalStatus.ProposalInAnalysis) },
];

const DEFAULT_SORT_FIELD = 'index_time';
const DEFAULT_SORT_TYPE = 'DESC';

const PROPOSAL_ANALYSES_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  proposerName: 'Nome',
  brokerName: 'AgentName',
  product: 'ProductName',
  proposalNumber: 'ProposalNo',
};

const PROPOSAL_ANALYSES_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'proposerName',
  AgentName: 'brokerName',
  ProductName: 'product',
  ProposalNo: 'proposalNumber',
};

export function ProposalAnalysesContent() {
  const navigate = useNavigate();

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });

  const { data: products } = useProducts();
  const productOptions = useMemo(
    () => [
      { label: 'Todos os produtos', value: '' },
      ...(products ?? []).map(product => ({
        label: product.name,
        value: product.code,
      })),
    ],
    [products]
  );

  const { data, isLoading, error } = useProposalAnalyses({
    pageNo: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    search: debouncedFilters.search,
    status: debouncedFilters.status,
    productCode: debouncedFilters.productCode,
    proposalDateFrom: debouncedFilters.dateFrom || undefined,
    proposalDateTo: debouncedFilters.dateTo || undefined,
    sortField: filters.sortField,
    sortType: filters.sortType,
  });

  const handleAction = (
    _action: ProposalAnalysisAction,
    proposal: ProposalAnalysisListItem
  ) => {
    void navigate({
      to: '/proposal-analyses/$proposalId',
      params: { proposalId: proposal.id },
    });
  };

  const columns = createProposalAnalysesColumns(handleAction);

  const proposals = data?.data ?? [];
  const totalPages = data?.meta.totalPages ?? 0;
  const totalItems = data?.meta.total ?? 0;
  const sorting = useMemo(() => {
    const columnId = PROPOSAL_ANALYSES_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'DESC',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <>
      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-4xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, status: value, pageNo: 1 })
            }
          />
          <SelectFilterField
            {...registerFilter('productCode')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, productCode: value, pageNo: 1 })
            }
          />
          <DateRangeFilterField
            from={filters.dateFrom}
            to={filters.dateTo}
            placeholder='Selecione a data'
            className='w-full md:min-w-[200px]'
            onChange={range =>
              setFilters({
                ...filters,
                dateFrom: range.from,
                dateTo: range.to,
                pageNo: 1,
              })
            }
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar propostas em análise. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-4 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                  (_, index) => (
                    <div
                      key={index}
                      className='h-60 animate-pulse rounded-3xl bg-alba-gray-200'
                    />
                  )
                )
              ) : proposals.length === 0 ? (
                <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhuma proposta em análise encontrada.
                </div>
              ) : (
                proposals.map(proposal => {
                  const brokerName = proposal.brokerName
                    ? proposal.brokerName.split('\n')[0]
                    : '-';

                  return (
                    <article
                      key={proposal.id}
                      className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <Avatar.Root
                            size='md'
                            shape='circle'
                            className='bg-alba-blue-500'
                          >
                            <User className='h-4 w-4 text-white' />
                          </Avatar.Root>
                          <div className='min-w-0'>
                            <p className='truncate font-gotham font-bold text-alba-black-500'>
                              {proposal.proposerName}
                            </p>
                            <p className='mt-1 text-xs text-alba-gray-600'>
                              {formatRegisterNumber(proposal.proposerDocument)}
                            </p>
                          </div>
                        </div>
                        <Menu.Root>
                          <Menu.Trigger />
                          <Menu.Content>
                            <Menu.Item
                              value='analysis'
                              icon={<SquarePen size={16} strokeWidth={1.6} />}
                              onSelect={() => handleAction('analysis', proposal)}
                            >
                              Análise
                            </Menu.Item>
                          </Menu.Content>
                        </Menu.Root>
                      </div>

                      <div className='mt-4 space-y-2'>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Corretor
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {brokerName}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Produto
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {proposal.product}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Nº da Proposta
                          </p>
                          <div className='flex flex-col items-end gap-0.5 text-right'>
                            <p className='text-sm text-alba-gray-700'>
                              {proposal.proposalNumber}
                            </p>
                            <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
                              <CalendarDays className='h-3.5 w-3.5' />
                              <span>{formatDateToBrazil(proposal.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Status
                          </p>
                          <Tag.Root variant='outline' colorScheme='yellow' size='sm'>
                            <Tag.Label>Em Análise</Tag.Label>
                          </Tag.Root>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}

              {totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={totalPages * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={totalItems}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        pageNo: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                className='min-w-0'
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={totalPages}
                data={proposals}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? PROPOSAL_ANALYSES_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    pageNo: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'DESC' : 'ASC',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
    </>
  );
}
